import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { apprenticeApi } from '../api/apprentices';

interface Standard {
  id: string;
  ifate_reference: string;
  title: string;
  level: number;
  knowledge: any[];
  skills: any[];
  behaviours: any[];
}

export const StandardsList: React.FC = () => {
  const { user, userRole } = useAuth();
  const [standards, setStandards] = useState<Standard[]>([]);
  const [filteredStandards, setFilteredStandards] = useState<Standard[]>([]);
  const [expandedStandard, setExpandedStandard] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'knowledge' | 'skills' | 'behaviours'>('skills');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<string>('');

  const loadStandards = useCallback(async () => {
    if (!user?.tenant_id) return;

    try {
      setLoading(true);

      if (userRole === 'learner') {
        // Learners see only their own standard
        const apprenticesResponse = await apprenticeApi.getAll(user.tenant_id);
        const myApprentice = apprenticesResponse.data.find(
          (a: any) => a.canvas_user_id === user.user_id || a.email === user.email
        );

        if (myApprentice?.standard_id) {
          const standardResponse = await axios.get(`/api/standards/${myApprentice.standard_id}`);
          const standard = standardResponse.data;

          setStandards([standard]);
          setFilteredStandards([standard]);
          setExpandedStandard(standard.id);
        } else {
          setStandards([]);
          setFilteredStandards([]);
        }
      } else if (userRole === 'instructor') {
        // Instructors see only standards for apprentices in their caseload
        const apprenticesResponse = await apprenticeApi.getAll(
          user.tenant_id,
          undefined,
          user.email
        );
        
        // Get unique standard IDs from their caseload
        const standardIds = new Set(
          apprenticesResponse.data
            .filter((a: any) => a.standard_id)
            .map((a: any) => a.standard_id)
        );

        if (standardIds.size > 0) {
          // Fetch all standards for the tenant, then filter
          const allStandardsResponse = await axios.get(`/api/standards/tenant/${user.tenant_id}`);
          const relevantStandards = allStandardsResponse.data.filter(
            (s: Standard) => standardIds.has(s.id)
          );
          
          setStandards(relevantStandards);
          setFilteredStandards(relevantStandards);
        } else {
          setStandards([]);
          setFilteredStandards([]);
        }
      } else {
        // Administrators see all standards
        const response = await axios.get(`/api/standards/tenant/${user.tenant_id}`);
        setStandards(response.data);
        setFilteredStandards(response.data);
      }
    } catch (error) {
      console.error('Failed to load standards:', error);
      setStandards([]);
      setFilteredStandards([]);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id, user?.user_id, user?.email, userRole]);

  useEffect(() => {
    loadStandards();
  }, [loadStandards]);

  useEffect(() => {
    let filtered = standards;

    if (searchTerm) {
      filtered = filtered.filter(
        (standard) =>
          standard.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          standard.ifate_reference.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedLevel) {
      filtered = filtered.filter(
        (standard) => standard.level === parseInt(selectedLevel)
      );
    }

    setFilteredStandards(filtered);
  }, [searchTerm, selectedLevel, standards]);

  const toggleExpand = (standardId: string) => {
    setExpandedStandard(expandedStandard === standardId ? null : standardId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading standards...</div>
      </div>
    );
  }

  if ((userRole === 'learner' || userRole === 'instructor') && standards.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">
            {userRole === 'learner' ? 'No Standard Assigned' : 'No Standards in Your Caseload'}
          </h2>
          <p className="text-yellow-700">
            {userRole === 'learner' 
              ? "You don't have an apprenticeship standard assigned yet. Please contact your administrator."
              : "You don't have any apprentices assigned yet. Contact an administrator to assign apprentices to your caseload."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {userRole === 'learner' 
            ? 'My Apprenticeship Standard' 
            : userRole === 'instructor'
            ? 'My Programme Standards'
            : 'Apprenticeship Standards'}
        </h1>
        <p className="text-gray-600">
          {userRole === 'learner' 
            ? 'View the Knowledge, Skills, and Behaviours (KSBs) for your apprenticeship'
            : userRole === 'instructor'
            ? `Standards for the ${standards.length} programme${standards.length !== 1 ? 's' : ''} in your caseload`
            : `Browse ${standards.length} UK apprenticeship standards`
          }
        </p>
      </div>

      {userRole === 'administrator' && (
        <div className="mb-6 flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by title or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Levels</option>
            <option value="2">Level 2</option>
            <option value="3">Level 3</option>
            <option value="4">Level 4</option>
            <option value="5">Level 5</option>
            <option value="6">Level 6</option>
            <option value="7">Level 7</option>
          </select>
        </div>
      )}

      <div className="space-y-4">
        {filteredStandards.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-600">No standards found matching your criteria.</p>
          </div>
        ) : (
          filteredStandards.map((standard) => {
            const knowledgeCount = Array.isArray(standard.knowledge) ? standard.knowledge.length : 0;
            const skillsCount = Array.isArray(standard.skills) ? standard.skills.length : 0;
            const behavioursCount = Array.isArray(standard.behaviours) ? standard.behaviours.length : 0;
            const isExpanded = expandedStandard === standard.id;
            
            return (
              <div
                key={standard.id}
                className="bg-white rounded-lg shadow hover:shadow-md transition"
              >
                <div 
                  className="p-6 cursor-pointer"
                  onClick={() => toggleExpand(standard.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {standard.ifate_reference}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Level {standard.level}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {standard.title}
                      </h3>
                      <div className="flex gap-6 text-sm text-gray-600">
                        <div className="flex items-center">
                          <span className="font-medium mr-1">Knowledge:</span>
                          <span className={knowledgeCount > 0 ? 'text-green-600' : 'text-gray-400'}>
                            {knowledgeCount}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium mr-1">Skills:</span>
                          <span className={skillsCount > 0 ? 'text-green-600' : 'text-gray-400'}>
                            {skillsCount}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium mr-1">Behaviours:</span>
                          <span className={behavioursCount > 0 ? 'text-green-600' : 'text-gray-400'}>
                            {behavioursCount}
                          </span>
                        </div>
                        <div className="flex items-center font-semibold">
                          <span className="mr-1">Total KSBs:</span>
                          <span className="text-blue-600">
                            {knowledgeCount + skillsCount + behavioursCount}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <svg 
                        className={`w-6 h-6 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-200 p-6">
                    <div className="flex border-b border-gray-200 mb-4">
                      {knowledgeCount > 0 && (
                        <button
                          onClick={() => setActiveTab('knowledge')}
                          className={`px-4 py-2 font-medium text-sm ${
                            activeTab === 'knowledge'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Knowledge ({knowledgeCount})
                        </button>
                      )}
                      {skillsCount > 0 && (
                        <button
                          onClick={() => setActiveTab('skills')}
                          className={`px-4 py-2 font-medium text-sm ${
                            activeTab === 'skills'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Skills ({skillsCount})
                        </button>
                      )}
                      {behavioursCount > 0 && (
                        <button
                          onClick={() => setActiveTab('behaviours')}
                          className={`px-4 py-2 font-medium text-sm ${
                            activeTab === 'behaviours'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Behaviours ({behavioursCount})
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      {activeTab === 'knowledge' && knowledgeCount > 0 && (
                        standard.knowledge.map((item: any, index: number) => (
                          <div key={index} className="p-4 bg-blue-50 rounded-lg">
                            <div className="flex items-start">
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-semibold mr-3 mt-0.5">
                                K{index + 1}
                              </span>
                              <p className="text-gray-800 flex-1">{item.detail}</p>
                            </div>
                          </div>
                        ))
                      )}

                      {activeTab === 'skills' && skillsCount > 0 && (
                        standard.skills.map((item: any, index: number) => (
                          <div key={index} className="p-4 bg-green-50 rounded-lg">
                            <div className="flex items-start">
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-green-600 text-white rounded-full text-xs font-semibold mr-3 mt-0.5">
                                S{index + 1}
                              </span>
                              <p className="text-gray-800 flex-1">{item.detail}</p>
                            </div>
                          </div>
                        ))
                      )}

                      {activeTab === 'behaviours' && behavioursCount > 0 && (
                        standard.behaviours.map((item: any, index: number) => (
                          <div key={index} className="p-4 bg-purple-50 rounded-lg">
                            <div className="flex items-start">
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-600 text-white rounded-full text-xs font-semibold mr-3 mt-0.5">
                                B{index + 1}
                              </span>
                              <p className="text-gray-800 flex-1">{item.detail}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {userRole === 'administrator' && (
        <div className="mt-6 text-sm text-gray-600">
          Showing {filteredStandards.length} of {standards.length} standards
        </div>
      )}
    </div>
  );
};
